import AppLayout from "@/layouts/AppLayout";
import { useUniversityNavLinks } from "@/pages/education/universities/[universityId]/_universityNav";
import { Outlet, useLocation, useOutletContext, useParams } from "react-router-dom";
import UniversityHeading from "../_layouts/UniversityHeading";
import { useQuery } from "@apollo/client";
import { UNIVERSITY_QUERY } from "../queries";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import Breadcrumb from "@/components/Breadcrumb";
import { EventsBasePathProvider } from "@/pages/events/_EventsBasePath";
import type { StudentProgramSummary } from "../../types";

export type UniversityEventsOutletContext = {
  universityName?: string;
  logoUrl?: string | null;
  activeStudentCount?: number;
  alumniStudentCount?: number;
  studentProgramSummary?: StudentProgramSummary[];
  universityId?: string;
};

export default function UniversityEventsLayout() {
  const { universityId } = useParams();
  const { isCompactLayout } = useIsCompactLayout();
  const parentContext = useOutletContext<UniversityEventsOutletContext>();
  const { pathname } = useLocation();

  const { data: universityData } = useQuery(UNIVERSITY_QUERY, {
    variables: { legacyId: universityId },
    skip: !universityId,
    errorPolicy: "all"
  });

  const university = universityData?.universities[0];
  const navLinks = useUniversityNavLinks(universityId!);

  const universityEventsBasePath = `/education/universities/${universityId}/events`;
  const isManualRegistrations = pathname.includes("/manual-registrations");
  const isFinalizeAttendance = pathname.endsWith("/attendance/finalize");
  const isFocusedRoute = isManualRegistrations || isFinalizeAttendance;
  const renderDesktopLayout = () => (
    <>
      <UniversityHeading
        universityName={university?.name || ""}
        logoUrl={university?.logoUrl}
        activeStudentCount={university?.activeStudentCount}
        alumniStudentCount={university?.alumniStudentCount}
        studentProgramSummary={university?.studentProgramSummary}
      />
      <Outlet context={parentContext} />
    </>
  );

  const renderMobileLayout = () => (
    <>
      <Breadcrumb
        compactOnly
        compactTitle="Events"
        backLink={{ to: "/education/universities/:universityId", params: { universityId: universityId! } }}
      />
      <UniversityHeading
        universityName={university?.name || ""}
        logoUrl={university?.logoUrl}
        activeStudentCount={university?.activeStudentCount}
        alumniStudentCount={university?.alumniStudentCount}
        studentProgramSummary={university?.studentProgramSummary}
      />
      <Outlet context={parentContext} />
    </>
  );
  if (isFocusedRoute) {
    return (
      <EventsBasePathProvider basePath={universityEventsBasePath}>
        <Outlet context={parentContext} />
      </EventsBasePathProvider>
    );
  }

  return (
    <EventsBasePathProvider basePath={universityEventsBasePath}>
      <AppLayout navLinks={navLinks} interior bottomBar>
        {isCompactLayout ? renderMobileLayout() : renderDesktopLayout()}
      </AppLayout>
    </EventsBasePathProvider>
  );
}
